import React, { useEffect, useState } from 'react';

import { DeleteRounded, LinkRounded } from '@mui/icons-material';
import { Stack } from '@mui/material';
import { useNavigate } from 'react-router';

import Breadcrumbs from '../../../components/Breadcrumbs';
import Button from '../../../components/Button';
import CommitForm from '../../../components/CommitForm';
import ContentGroupsList from '../../../components/ContentGroupsList';
import ContentWrapper from '../../../components/ContentWrapper';
import CopyButton from '../../../components/CopyButton';
import DetailsModal, { Values } from '../../../components/DetailsModal';
import Modal from '../../../components/Modal';
import SkeletonStack from '../../../components/SkeletonStack';
import {
  useDeleteVideoCategoryMutation,
  useGetVideoCategoryQuery,
  useNewVideoTopicMutation,
  useUpdateVideoCategoryMutation,
} from '../../../services/videos';
import { Topic } from '../../../services/videos/types';
import { SUBJECTS } from '../../../utils/common/constants';
import useError from '../../../utils/react/hooks/useError';
import useSuccess from '../../../utils/react/hooks/useSuccess';
import {
  BreadcrumbsContainer,
} from '../index.styles';
import ShareButton from '../ShareButton';

const INITIAL_TOPIC_FORM = {
  key: '',
  label: '',
  shortLabel: '',
};

interface CategoryPageProps {
  subject: string,
  category: string
}

const CategoryPage = ({ subject, category }: CategoryPageProps) => {
  const navigate = useNavigate();

  const { data: categoryData, isLoading } = useGetVideoCategoryQuery(
    { subject, category }, { skip: !subject || !category },
  );
  const [newTopicForm, setNewTopicForm] = useState(INITIAL_TOPIC_FORM);
  const [newTopicFormOpen, setNewTopicFormOpen] = useState<boolean>(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);

  const [newTopic, {
    isSuccess: isNewTopicSuccess,
    isLoading: isNewTopicLoading,
    error: newTopicError,
    reset: resetNewTopic,
  }] = useNewVideoTopicMutation();
  const [updateCategory, {
    isSuccess: isUpdateCategorySuccess,
    isLoading: isUpdateCategoryLoading,
    isError: isUpdateCategoryError,
    error: updateCategoryError,
    reset: resetUpdateCategory,
  }] = useUpdateVideoCategoryMutation();
  const [deleteCategory, {
    isSuccess: isDeleteCategorySuccess,
    isLoading: isDeleteCategoryLoading,
    isError: isDeleteCategoryError,
    error: deleteCategoryError,
  }] = useDeleteVideoCategoryMutation();

  const resetModal = async () => {
    await resetNewTopic();
    setNewTopicForm(INITIAL_TOPIC_FORM);
    await resetUpdateCategory();
  };

  const onTopicEntryCreate = (values?: Values) => {
    newTopic({
      subject,
      category,
      attributes: values,
    });
  };

  useEffect(() => {
    if (isDeleteCategorySuccess) {
      // Navigate up a level after a category is deleted
      navigate(`/admin/videos/${subject}`);
    }
  }, [isDeleteCategorySuccess]);

  // Close modal after topic is created
  useEffect(() => {
    if (isNewTopicSuccess) {
      setNewTopicFormOpen(false);
      resetModal();
    }
  }, [isNewTopicSuccess]);

  useSuccess(isDeleteCategorySuccess, 'Category deleted');
  useError(isUpdateCategoryError, updateCategoryError);
  useError(isDeleteCategoryError, deleteCategoryError);

  return (
    <div>
      {!isLoading && categoryData?.data ? (
        <>
          <BreadcrumbsContainer>
            <Breadcrumbs
              title={categoryData?.data.shortLabel}
              path={[
                {
                  label: SUBJECTS[categoryData?.data.subject]?.label || 'Videos',
                  href: `/admin/videos/${categoryData?.data.subject}`,
                },
              ]}
            />
          </BreadcrumbsContainer>
          <ContentWrapper title="Category Details">
            <CommitForm
              data={categoryData?.data}
              metadata={categoryData?.metadata}
              fields={{
                category: {
                  xs: 12,
                  md: 12,
                },
                label: {
                  xs: 12,
                  md: 12,
                },
                shortLabel: {
                  xs: 12,
                  md: 12,
                },
              }}
              onSubmit={async (values) => updateCategory({
                subject,
                category,
                attributes: values,
              })}
              isSubmitLoading={isUpdateCategoryLoading}
              autoSubmit
            />
          </ContentWrapper>
          <ContentWrapper title="Actions">
            <Stack direction="row" spacing={1}>
              <Button
                color="error"
                size="medium"
                onClick={() => setDeleteModalOpen(true)}
                disabled={categoryData?.data.topics.length > 0}
                startIcon={(<DeleteRounded />)}
              >
                Delete
              </Button>
              <ShareButton
                targetTitle={categoryData?.data.shortLabel}
                subject={subject}
                category={category}
              />
              <CopyButton
                size="medium"
                copyText={`${window.location.origin}/videos?subject=${subject}&category=${category}`}
                startIcon={(<LinkRounded />)}
                buttonLabel="Copy Link"
              />
            </Stack>
          </ContentWrapper>
          <ContentWrapper title="Topics">
            <ContentGroupsList
              columns={[
                {
                  field: 'key',
                  headerName: 'Key',
                  width: 120,
                },
                {
                  field: 'label',
                  headerName: 'Topic',
                  width: 360,
                },
                {
                  field: 'videosCount',
                  headerName: 'Videos',
                  width: 120,
                },
              ]}
              data={categoryData?.data?.topics.map((topic) => ({
                ...topic, videosCount: topic.videos.length, id: topic.key,
              })) || []}
              loading={isLoading}
              actions={{
                create: {
                  fn: () => setNewTopicFormOpen(true),
                  label: 'Add Topic',
                },
                reorder: {
                  fn: async (fields) => updateCategory({
                    subject,
                    category,
                    attributes: {
                      topics: fields?.orderedData.map((x: Topic) => x.key),
                    },
                  }),
                  label: 'Reorder',
                  isSuccess: isUpdateCategorySuccess,
                  isLoading: isUpdateCategoryLoading,
                  error: updateCategoryError,
                },
              }}
              getRowHref={(row: Topic) => row?.key}
            />
          </ContentWrapper>
          <DetailsModal
            open={newTopicFormOpen}
            handleClose={() => setNewTopicFormOpen(false)}
            error={newTopicError}
            title="Add New Topic"
            fields={{
              key: {
                editable: true,
                type: 'text',
                label: '4-Char Key',
              },
              label: {
                editable: true,
                type: 'text',
              },
              shortLabel: {
                editable: true,
                type: 'text',
              },
            }}
            values={newTopicForm}
            buttons={[{
              fn: onTopicEntryCreate,
              label: 'Add',
              loading: isNewTopicLoading,
            }]}
          />
          <Modal
            open={deleteModalOpen}
            handleClose={() => setDeleteModalOpen(false)}
            title="Are you sure?"
            description="This will permanently delete this category."
            buttons={[
              {
                label: 'Delete',
                onClick: () => deleteCategory({ subject, category }),
                color: 'error',
                loading: isDeleteCategoryLoading,
              },
            ]}
          />
        </>
      ) : (
        <SkeletonStack />
      )}
    </div>
  );
};

export default CategoryPage;
